import { create } from 'zustand';
import { z } from 'zod';
import type { TimeTrackingEntryResponse, TimerPreset } from '@proerp/shared';
import { timerPresetSchema } from '@proerp/shared';

/**
 * Der „Fortsetzen"-Vorschlag nach dem Stoppen (VB-26027).
 *
 * Seit der Timer in der Kopfzeile sitzt (2026-09-16) sind Stoppen und Vorschlag
 * zwei verschiedene Stellen im Baum: Gestoppt wird in der Pille (`TimerPill`),
 * angeboten wird unten rechts (`ResumeTimerCard`). Der Vorschlag lebt deshalb
 * hier in einem eigenen Store statt im State einer der beiden Komponenten.
 *
 * Zusätzlich im localStorage, damit er einen Seitenwechsel und ein Neuladen
 * übersteht: Wer unterbricht, klickt danach durch die App und soll den Vorschlag
 * dort wiederfinden.
 */
const RESUME_KEY = 'proerp.floating-timer.resume.v1';

/**
 * Verfallsdauer des Vorschlags: 15 Minuten ab dem Stoppen.
 *
 * Bewusst kurz. Ein Vorschlag, der nachmittags noch anbietet, das
 * Vormittagsprojekt fortzusetzen, wäre eine Falle — ein Klick bucht dann auf ein
 * Projekt, an dem längst niemand mehr arbeitet. 15 Minuten decken die echten
 * Unterbrechungen ab (Anruf, Zwischenfrage, kurzer Gang zur Werkstatt) und sind
 * kurz genug, dass „Fortsetzen" noch stimmt. Danach führt der Weg über die
 * Zeiterfassungsseite mit „Zuletzt verwendet" — dort ist die Auswahl bewusst.
 */
export const RESUME_TTL_MS = 15 * 60_000;

const resumeSuggestionSchema = z.object({
  /** Die startbereite Kombination — dasselbe Format wie „Zuletzt verwendet". */
  preset: timerPresetSchema,
  stoppedAt: z.string().datetime(),
});
export type ResumeSuggestion = z.infer<typeof resumeSuggestionSchema>;

export function expiresAt(suggestion: ResumeSuggestion): number {
  return new Date(suggestion.stoppedAt).getTime() + RESUME_TTL_MS;
}

/** Gespeicherten Vorschlag lesen — kaputte, fremde oder abgelaufene Stände verwerfen. */
export function loadResumeSuggestion(): ResumeSuggestion | null {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const parsed = resumeSuggestionSchema.safeParse(JSON.parse(raw) as unknown);
    if (!parsed.success || expiresAt(parsed.data) <= Date.now()) {
      localStorage.removeItem(RESUME_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function storeResumeSuggestion(suggestion: ResumeSuggestion | null): void {
  try {
    if (suggestion) localStorage.setItem(RESUME_KEY, JSON.stringify(suggestion));
    else localStorage.removeItem(RESUME_KEY);
  } catch {
    // Merken ist best effort — der Store hält den Vorschlag auch ohne Storage.
  }
}

/**
 * Baut aus dem laufenden Eintrag die startbereite Kombination für „Fortsetzen".
 *
 * Fehlt eine der drei Pflichtangaben des Starts (Firma, Kategorie, Projekt) —
 * etwa bei Alt-Einträgen aus der Zeit vor den Kategorien —, entsteht kein
 * Vorschlag: Ein Klick liefe sonst garantiert in einen Serverfehler.
 */
export function toResumePreset(entry: TimeTrackingEntryResponse): TimerPreset | null {
  // Zeit-Projekt schlägt ProERP-Projekt: Bei einem Hybrid-Zeit-Projekt sind beide
  // IDs gesetzt, gewählt hat der Nutzer aber das Zeit-Projekt.
  const projectId = entry.ttProjectId ?? entry.projectId;
  const projectName = entry.ttProjectName ?? entry.projectName;
  if (!entry.legalEntityId || !entry.ttCategoryId || !projectId) return null;
  return {
    legalEntityId: entry.legalEntityId,
    legalEntityName: entry.legalEntityName ?? '',
    categoryId: entry.ttCategoryId,
    categoryName: entry.ttCategoryName ?? '',
    projectKind: entry.ttProjectId ? 'tt' : 'project',
    projectId,
    projectName: projectName ?? '',
    projectCode: entry.projectCode,
    ttSubCategoryId: entry.ttSubCategoryId,
    ttSubCategoryName: entry.ttSubCategoryName,
    note: entry.note,
  };
}

interface ResumeState {
  suggestion: ResumeSuggestion | null;
  /** Nach dem Stoppen merken — ohne verwertbares Preset passiert nichts. */
  remember: (entry: TimeTrackingEntryResponse) => void;
  dismiss: () => void;
}

export const useResumeSuggestion = create<ResumeState>((set) => ({
  // Beim ersten Laden aus dem Storage — abgelaufene Stände fallen dabei raus.
  suggestion: loadResumeSuggestion(),
  remember: (entry) => {
    const preset = toResumePreset(entry);
    if (!preset) return;
    const suggestion: ResumeSuggestion = { preset, stoppedAt: new Date().toISOString() };
    storeResumeSuggestion(suggestion);
    set({ suggestion });
  },
  dismiss: () => {
    storeResumeSuggestion(null);
    set({ suggestion: null });
  },
}));
